import { BulkContractEnrollmentSummaryRecord } from "models/view-models/enrollments/bulk-contract-enrollment-summary-record";
import {
    CountEnrollmentsQueryParams,
    EnrollmentService,
    WithdrawEnrollmentBodyParams,
    WithdrawEnrollmentPathParams,
} from "utilities/services/enrollments/enrollment-service";
import { CreateBulkEnrollmentDtoRecord } from "models/view-models/enrollments/create-bulk-enrollment-params-record";
import { CreateEnrollmentDtoRecord } from "models/view-models/enrollments/create-enrollment-dto-record";
import { EncodingType } from "models/enumerations/encoding/encoding-type";
import { EnrollmentRecord } from "models/view-models/enrollments/enrollment-record";
import { FileUtils } from "utilities/files/file-utils";
import {
    ListProductEnrollmentSummariesPathParams,
    ListProductEnrollmentSummariesQueryParams,
    ProductEnrollmentSummariesService,
} from "utilities/services/enrollments/product-enrollment-summaries-service";
import { NumberUtils } from "utilities/number-utils";
import { OllContractEnrollmentService } from "utilities/services/enrollments/oll-contract-enrollment-service";
import { ProductEnrollmentSummaryRecord } from "models/view-models/enrollments/product-enrollment-summary-record";
import { ResultErrorRecord, ResultRecord } from "andculturecode-javascript-core";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization/t";
import { useCallback, useEffect, useState } from "react";

interface useOLLContractEnrollmentsHook {
    enrollmentSummaries: ProductEnrollmentSummaryRecord[];
    createEnrollment: (createEnrollmentDto?: CreateEnrollmentDtoRecord) => Promise<boolean>;
    createBulkEnrollments: (
        createBulkEnrollmentDto: CreateBulkEnrollmentDtoRecord
    ) => Promise<ResultRecord<BulkContractEnrollmentSummaryRecord>>;
    withdrawEnrollment: (enrollment: EnrollmentRecord, sendEmailToLearner: boolean) => Promise<boolean>;
    isLoading: boolean;
    errors?: ResultErrorRecord[];
    usedEnrollmentCount: number;
    rowCount: number;
}

interface useOLLContractEnrollmentsHookOptions {
    contractId?: number | string;
    productId?: number | string;
    includeProduct?: boolean;
    searchText?: string;
    skip?: number;
    take?: number;
}

export function useOLLContractEnrollments(
    props: useOLLContractEnrollmentsHookOptions
): useOLLContractEnrollmentsHook {
    const encodingType: EncodingType = FileUtils.getEncodingType();
    const [rowCount, setRowCount] = useState(0);
    const [errors, setErrors] = useState<ResultErrorRecord[]>();
    const [isLoading, setIsLoading] = useState(true);

    const [enrollmentSummaries, setEnrollmentSummaries] = useState<
        ProductEnrollmentSummaryRecord[]
    >([]);
    const [usedEnrollmentCount, setUsedEnrollmentCount] = useState(0);
    const contractId = NumberUtils.parseInt(props.contractId) ?? 0;
    const productId = NumberUtils.parseInt(props.productId) ?? 0;
    const searchText = props.searchText;
    const skip = props.skip;
    const take = props.take;

    const { create: enrollmentCreate } = OllContractEnrollmentService.useCreate();
    const { create: bulkEnrollmentCreate } = OllContractEnrollmentService.useBulkCreate();
    const { patch: withdrawEnrollmentApi } = EnrollmentService.useWithdrawEnrollment();
    const { list: listProductEnrollmentSummaryApi } = ProductEnrollmentSummariesService.useList();
    const { get: countEnrollmentsApi } = EnrollmentService.useCount();

    const fetchData = useCallback(async () => {
        if (isNaN(contractId) || contractId < 1 || isNaN(productId) || productId < 1) {
            return;
        }

        try {
            (async function getEnrollmentSummaries() {
                const listProductEnrollmentSummariesPathParams: ListProductEnrollmentSummariesPathParams =
                    {
                        productId: productId,
                    };

                const listProductEnrollmentSummariesQueryParams: ListProductEnrollmentSummariesQueryParams =
                    { contractId: contractId, skip: skip, take: take };

                const countEnrollmentsQueryParams: CountEnrollmentsQueryParams = {
                    contractId: contractId,
                    isWithdrawn: false,
                };

                const [enrollmentSummaryResult, enrollmentCountResult] = await Promise.all([
                    listProductEnrollmentSummaryApi(
                        listProductEnrollmentSummariesPathParams,
                        listProductEnrollmentSummariesQueryParams
                    ),
                    countEnrollmentsApi({}, countEnrollmentsQueryParams),
                ]);

                const {
                    result: resultEnrollmentSummary,
                    resultObjects,
                    rowCount,
                } = enrollmentSummaryResult;

                const { result: resultEnrollmentCount } = enrollmentCountResult;

                setIsLoading(false);

                if (resultEnrollmentSummary?.hasErrors() || resultEnrollmentCount?.hasErrors()) {
                    const { errors: enrollmentSummaryErrors = [] } = resultEnrollmentSummary ?? {};
                    const { errors: enrollmentCountErrors = [] } = resultEnrollmentCount ?? {};

                    setErrors([...enrollmentSummaryErrors, ...enrollmentCountErrors]);
                    return;
                }

                setRowCount(rowCount);
                setEnrollmentSummaries(resultObjects);
                setUsedEnrollmentCount(resultEnrollmentCount?.resultObject ?? 0);
            })();
        } catch {
            ToastManager.error(t("thereWasAProblemReturningEnrollments"));
            setEnrollmentSummaries([]);
            setUsedEnrollmentCount(0);
        }
    }, [contractId, countEnrollmentsApi, listProductEnrollmentSummaryApi, productId, skip, take]);

    const createEnrollment = async (
        createEnrollmentDto?: CreateEnrollmentDtoRecord
    ): Promise<boolean> => {
        try {
            if (contractId <= 0 || productId <= 0 || createEnrollmentDto === undefined) {
                throw new Error(t("creatingAnEnrollmentFailed"));
            }

            createEnrollmentDto = createEnrollmentDto.with({ productId: productId });

            const createEnrollmentResponse = await enrollmentCreate(
                contractId,
                createEnrollmentDto
            );
            const createEnrollmentResult = createEnrollmentResponse?.result;

            if (
                createEnrollmentResult?.resultObject == null ||
                createEnrollmentResult.hasErrors()
            ) {
                throw new Error(t("thereWasAProblemCreatingANewEnrollment"));
            }

            // Retrieve the data.
            fetchData();

            return true;
        } catch (error) {
            if (typeof error === "string") {
                const errorMessage = error === "" ? t("failedToCreateANewEnrollment") : error;
                ToastManager.error(errorMessage);
            } else if (error instanceof ResultRecord) {
                const { errors } = error;
                errors?.forEach((error) => {
                    ToastManager.error(error.message);
                });
            }
            return false;
        }
    };

    const createBulkEnrollments = async (
        createBulkEnrollmentDto: CreateBulkEnrollmentDtoRecord
    ): Promise<ResultRecord<BulkContractEnrollmentSummaryRecord>> => {
        let result = new ResultRecord<BulkContractEnrollmentSummaryRecord>();

        if (
            contractId === undefined ||
            contractId <= 0 ||
            productId === undefined ||
            createBulkEnrollmentDto === undefined
        ) {
            result.addError("", t("contractIDProductIDAndFileAreNeeded"));
            return result;
        }

        createBulkEnrollmentDto = createBulkEnrollmentDto.with({ productId: productId });

        try {
            const createBulkEnrollmentResponse = await bulkEnrollmentCreate(
                contractId,
                encodingType,
                createBulkEnrollmentDto
            );
            const createBulkEnrollmentResult = createBulkEnrollmentResponse?.result;

            if (createBulkEnrollmentResult?.resultObject == null) {
                throw new Error();
            }

            result = createBulkEnrollmentResult;
        } catch (error) {
            try {
                result = error as ResultRecord<BulkContractEnrollmentSummaryRecord>;
            } catch (error: any) {
                result.addError("", error.toString());
                ToastManager.error(t("thereWasProblemSavingBulkEnrollments"));
            }
        }

        // Retrieve the data.
        fetchData();

        return result;
    };

    const withdrawEnrollment = async (enrollment: EnrollmentRecord, sendEmailToLearner: boolean): Promise<boolean> => {
        try {
            const pathParams: WithdrawEnrollmentPathParams = {
                id: enrollment.id!,
            };

            const bodyParams:WithdrawEnrollmentBodyParams = {
                withdrawnComment: enrollment.withdrawnComment,
                sendEmailToLearner: sendEmailToLearner
            };

            const withdrawEnrollmentResponse = await withdrawEnrollmentApi(pathParams, bodyParams);
            const withdrawResult = withdrawEnrollmentResponse?.result;
            if (withdrawResult?.resultObject == null || withdrawResult.hasErrors()) {
                throw new Error();
            }

            fetchData();
            ToastManager.success(t("withdrawEnrollmentSuccessful"));
            return true;
        } catch {
            ToastManager.error(t("thereWasAnIssueWithdrawingAnEnrollment"));
            return false;
        }
    };

    useEffect(() => {
        fetchData();
    }, [searchText, skip, take, contractId, productId, listProductEnrollmentSummaryApi, fetchData]);

    return {
        errors,
        isLoading,
        enrollmentSummaries,
        createBulkEnrollments,
        createEnrollment,
        withdrawEnrollment,
        rowCount,
        usedEnrollmentCount,
    };
}
